import React, { useCallback, useEffect, useState } from 'react';
import { InputField } from '@saviynt/design-system';

import { clampInterval } from '../../utils/schedulingUtils';

interface SchedulingIntervalInputProps {
  /** Disable input field */
  disabled: boolean;
  /** Current interval value */
  value: number;
  /** Callback invoked with new clamped value */
  onChange: (value: number) => void;
  /** Callback invoked when input is empty */
  onEmptyChange?: (isEmpty: boolean) => void;
  /** Test ID for the input field */
  dataTestId?: string;
}

/**
 * SchedulingIntervalInput
 * -----------------------
 * Controlled number input that parses and clamps user input with max value of 52.
 */
const SchedulingIntervalInput = React.memo<SchedulingIntervalInputProps>(
  ({ disabled, value, onChange, onEmptyChange, dataTestId }) => {
    const [displayValue, setDisplayValue] = useState(value.toString());

    useEffect(() => {
      setDisplayValue(value.toString());
    }, [value]);

    const handleSetValue = useCallback(
      (inputValue: string) => {
        const isEmpty = inputValue === '';

        onEmptyChange?.(isEmpty);

        if (isEmpty) {
          setDisplayValue(inputValue);

          return; // Allow empty state
        }

        const num = Number(inputValue);

        if (!Number.isNaN(num)) {
          const cappedValue = num > 4 ? '4' : inputValue;

          setDisplayValue(cappedValue);
          onChange(clampInterval(Math.min(num, 4)));
        }
      },
      [onChange, onEmptyChange]
    );

    return (
      <InputField
        id='repeatEvery'
        type='text'
        inputmode='numeric'
        name='repeatEvery'
        placeholder='Enter number'
        value={displayValue}
        setValue={handleSetValue}
        regexPattern='[0-9]*'
        isDisabled={disabled}
        aria-required='true'
        aria-label='Repeat interval'
        dataTestId={dataTestId}
      />
    );
  }
);

SchedulingIntervalInput.displayName = 'SchedulingIntervalInput';

export default SchedulingIntervalInput;
